import { forwardRef, useId, type InputHTMLAttributes, type TextareaHTMLAttributes, type SelectHTMLAttributes, type ReactNode } from 'react'
import { cn } from '../../lib/cn'

/** Bare input (control radius). Use FloatingInput when the field has a label. */
export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(function Input({ className, ...props }, ref) {
  return (
    <input
      ref={ref}
      className={cn(
        'h-10 w-full min-w-0 rounded-lg border border-input bg-transparent px-3 py-1 text-base transition-colors outline-none placeholder:text-muted-foreground focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50 aria-invalid:border-destructive aria-invalid:ring-3 aria-invalid:ring-destructive/20 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm',
        className,
      )}
      {...props}
    />
  )
})

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaHTMLAttributes<HTMLTextAreaElement>>(function Textarea({ className, ...props }, ref) {
  return (
    <textarea
      ref={ref}
      className={cn(
        'min-h-24 w-full rounded-lg border border-input bg-transparent px-3 py-2 text-base transition-colors outline-none placeholder:text-muted-foreground focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50 aria-invalid:border-destructive disabled:cursor-not-allowed disabled:opacity-50 md:text-sm',
        className,
      )}
      {...props}
    />
  )
})

export const Select = forwardRef<HTMLSelectElement, SelectHTMLAttributes<HTMLSelectElement>>(function Select({ className, ...props }, ref) {
  return (
    <select
      ref={ref}
      className={cn(
        'h-10 w-full rounded-lg border border-input bg-transparent px-3 text-sm transition-colors outline-none focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50 disabled:cursor-not-allowed disabled:opacity-50',
        className,
      )}
      {...props}
    />
  )
})

export function Label({ className, ...props }: React.LabelHTMLAttributes<HTMLLabelElement>) {
  return <label className={cn('text-sm font-medium', className)} {...props} />
}

/** Search field in a list header: pill, icon on the left. */
export function SearchInput({ className, icon, ...props }: InputHTMLAttributes<HTMLInputElement> & { icon?: ReactNode }) {
  return (
    <div className="relative">
      {icon ? <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground [&_svg]:size-4">{icon}</span> : null}
      <Input className={cn('h-10 rounded-full', icon ? 'pl-9' : undefined, className)} {...props} />
    </div>
  )
}

/* ── Floating (outlined, Material-style) fields — doWallet's labelled inputs ──
   fieldset/legend notch: the label sits on the border once the field has a
   value or focus. On focus the border and label take the text colour (2px):
   near-black in light mode, white in dark mode — no blue stroke. */
type FloatingSize = 'default' | 'auth'
const floatingHeight: Record<FloatingSize, string> = { default: 'h-10 px-3', auth: 'h-12 px-4 text-base' }

function FloatingFrame({
  id, label, size, invalid, hint, error, children, hasValue,
}: { id: string; label: string; size: FloatingSize; invalid?: boolean; hint?: ReactNode; error?: ReactNode; children: ReactNode; hasValue: boolean }) {
  return (
    <div className="group/ff w-full" data-filled={hasValue || undefined}>
      <div className="relative">
        {children}
        <fieldset
          aria-hidden
          className={cn(
            'pointer-events-none absolute inset-0 -top-[7px] rounded-lg border border-input px-2 transition-colors',
            'group-focus-within/ff:border-2 group-focus-within/ff:border-foreground',
            invalid && 'border-destructive group-focus-within/ff:border-destructive',
          )}
        >
          <legend className="ml-1 max-w-0 px-0 text-[11px] leading-none whitespace-nowrap opacity-0 transition-[max-width] group-focus-within/ff:max-w-full group-focus-within/ff:px-1 group-data-[filled]/ff:max-w-full group-data-[filled]/ff:px-1">
            {label}
          </legend>
        </fieldset>
        <label
          htmlFor={id}
          className={cn(
            'pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground transition-all',
            'group-focus-within/ff:top-0 group-focus-within/ff:-translate-y-1/2 group-focus-within/ff:text-xs group-focus-within/ff:text-foreground',
            'group-data-[filled]/ff:top-0 group-data-[filled]/ff:-translate-y-1/2 group-data-[filled]/ff:text-xs',
            size === 'auth' && 'left-4',
            invalid && 'text-destructive group-focus-within/ff:text-destructive',
          )}
        >
          {label}
        </label>
      </div>
      {error ? <p className="mt-1.5 text-xs text-destructive">{error}</p> : hint ? <p className="mt-1.5 text-xs text-muted-foreground">{hint}</p> : null}
    </div>
  )
}

export type FloatingInputProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> & { label: string; size?: FloatingSize; hint?: ReactNode; error?: ReactNode }

export const FloatingInput = forwardRef<HTMLInputElement, FloatingInputProps>(function FloatingInput(
  { label, size = 'default', hint, error, className, id, value, defaultValue, placeholder, ...props }, ref,
) {
  const autoId = useId()
  const fieldId = id ?? autoId
  const hasValue = value !== undefined ? String(value).length > 0 : defaultValue !== undefined && String(defaultValue).length > 0
  return (
    <FloatingFrame id={fieldId} label={label} size={size} invalid={!!error} hint={hint} error={error} hasValue={hasValue || !!placeholder}>
      <input
        ref={ref}
        id={fieldId}
        value={value}
        defaultValue={defaultValue}
        placeholder={placeholder}
        aria-invalid={error ? true : undefined}
        className={cn('peer w-full min-w-0 rounded-lg bg-transparent text-sm outline-none placeholder:text-muted-foreground/70 disabled:cursor-not-allowed disabled:opacity-50', floatingHeight[size], className)}
        {...props}
      />
    </FloatingFrame>
  )
})

export type FloatingTextareaProps = TextareaHTMLAttributes<HTMLTextAreaElement> & { label: string; hint?: ReactNode; error?: ReactNode }

export const FloatingTextarea = forwardRef<HTMLTextAreaElement, FloatingTextareaProps>(function FloatingTextarea(
  { label, hint, error, className, id, value, defaultValue, placeholder, ...props }, ref,
) {
  const autoId = useId()
  const fieldId = id ?? autoId
  const hasValue = value !== undefined ? String(value).length > 0 : defaultValue !== undefined && String(defaultValue).length > 0
  return (
    <FloatingFrame id={fieldId} label={label} size="default" invalid={!!error} hint={hint} error={error} hasValue={hasValue || !!placeholder}>
      <textarea
        ref={ref}
        id={fieldId}
        value={value}
        defaultValue={defaultValue}
        placeholder={placeholder}
        aria-invalid={error ? true : undefined}
        className={cn('peer min-h-28 w-full rounded-lg bg-transparent px-3 py-3 text-sm outline-none placeholder:text-muted-foreground/70 disabled:cursor-not-allowed disabled:opacity-50', className)}
        {...props}
      />
    </FloatingFrame>
  )
})

export type FloatingSelectProps = SelectHTMLAttributes<HTMLSelectElement> & { label: string; hint?: ReactNode; error?: ReactNode }

export const FloatingSelect = forwardRef<HTMLSelectElement, FloatingSelectProps>(function FloatingSelect(
  { label, hint, error, className, id, children, ...props }, ref,
) {
  const autoId = useId()
  const fieldId = id ?? autoId
  return (
    <FloatingFrame id={fieldId} label={label} size="default" invalid={!!error} hint={hint} error={error} hasValue>
      <select ref={ref} id={fieldId} className={cn('peer h-10 w-full rounded-lg bg-transparent px-3 text-sm outline-none disabled:cursor-not-allowed disabled:opacity-50', className)} {...props}>
        {children}
      </select>
    </FloatingFrame>
  )
})
